import React, { useRef, useEffect, useState } from "react";
import "../../styles/components/AnimationTimeLine.scss";

function Canvas(props) {
  let canvasRef = useRef(null);
  const [startX, setStartX] = useState(0);
  const [cursorOffset, setCursorOffset] = useState(0);
  const startXRef = useRef(0);
  const cursorOffsetRef = useRef(0);
  const draggerWidthRef = useRef(props.draggerWidth);
  const domIdPrefix = props.domIdPrefix || "";
  const resizableDivId = `${domIdPrefix}resizableDiv`;
  const isHitAreaModal = domIdPrefix === "hit-area-";

  useEffect(() => {
    // Prefer saved timeline width from asset hydration; do not force 675 on mount.
    const saved = Number(props.appData?.genericInfo?.draggerWidth);
    if (Number.isFinite(saved) && saved > 0) {
      props.setDraggerWidth(saved);
    }
  }, [props.appData?.genericInfo?.draggerWidth]);

  useEffect(() => {
    draggerWidthRef.current = props.draggerWidth;
  }, [props.draggerWidth]);

  // Keep resize tracking on document so the drag doesn't die when the pointer
  // leaves the thin timeline strip (that was causing the "stuck to the end" feel).
  useEffect(() => {
    if (!props.isResizing) return;

    const onMove = (event) => {
      const resizableDiv = document.getElementById(resizableDivId);
      if (!resizableDiv) return;

      // Keep the grabbed right edge locked to the cursor (no gap).
      const left = resizableDiv.getBoundingClientRect().left;
      let newWidth = event.clientX + cursorOffsetRef.current - left;

      const oneCM = props.totalDuration.divisions < 60 ? 45 : 44.97;
      const maxWidth = props.totalDuration.divisions * oneCM;
      if (newWidth < oneCM) newWidth = oneCM;
      if (newWidth > maxWidth) newWidth = maxWidth;

      draggerWidthRef.current = newWidth;
      startXRef.current = event.clientX;
      props.setDraggerWidth(newWidth);
    };

    const onUp = () => {
      props.setIsResizing(false);
      props.setMousUpOnTimeline(true);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      props.appData.updateGenericInfo({
        ...props.appData.genericInfo,
        draggerWidth: draggerWidthRef.current,
      });
    };

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [props.isResizing, props.totalDuration.divisions, resizableDivId]);

  useEffect(() => {
    let canvas = canvasRef.current;

    let propObj = {};
    propObj.AXIS_ORIGIN = { x: 0, y: canvas.height - 0 };
    propObj.TEXT_FONTSIZE = 10;
    propObj.TOTAL_DURATION = props.totalDuration.divisions;
    propObj.SECONDS_DIVISION = 5;
    propObj.HORIZONTAL_TICK_SPACING =
      (canvas.width - propObj.TEXT_FONTSIZE * 2) /
      propObj.TOTAL_DURATION /
      propObj.SECONDS_DIVISION;
    propObj.NUM_HORIZONTAL_TICKS =
      propObj.TOTAL_DURATION * propObj.SECONDS_DIVISION;
    propObj.TICKS_LINEWIDTH = 0.5;

    const context = canvasRef.current.getContext("2d");
    drawHorizontalAxisTicks();

    function drawHorizontalAxisTicks() {
      let j = 1;
      let moveText = 0;
      const context = canvas.getContext("2d");
      context.strokeStyle = "#000000";
      context.fillStyle = "#7B8496";

      for (var i = 0; i <= propObj.NUM_HORIZONTAL_TICKS; ++i) {
        context.beginPath();

        if (i !== 0 && i % 5 === 0) {
          const tickY = 28;
          const lineY = 50;

          context.moveTo(0 + propObj.HORIZONTAL_TICK_SPACING * i, lineY);
          context.lineTo(propObj.HORIZONTAL_TICK_SPACING * i, tickY);
          context.textAlign = "left";
          if (j > 9) moveText = 5;
          else moveText = 2;
          context.fillText(
            j++,
            propObj.HORIZONTAL_TICK_SPACING * i - moveText,
            tickY - 4
          );
        } else {
          const tickY = 38;

          context.moveTo(
            propObj.AXIS_ORIGIN.x + i * propObj.HORIZONTAL_TICK_SPACING,
            45
          );
          context.lineTo(
            propObj.AXIS_ORIGIN.x + i * propObj.HORIZONTAL_TICK_SPACING,
            tickY
          );
        }
        context.stroke();
      }

      const borderY = 28;
      context.beginPath();
      context.moveTo(propObj.AXIS_ORIGIN.x, borderY);
      context.lineTo(canvas.width, borderY);
      context.stroke();
    }
  }, [props.totalDuration.divisions]);

  const handleMouseDown = (event) => {
    const divRect = event.currentTarget.getBoundingClientRect();
    // Distance from pointer to the right edge — used so the edge stays under the grab.
    const offset = divRect.right - event.clientX;
    cursorOffsetRef.current = offset;
    setCursorOffset(offset);

    if (
      event.clientX >
      canvasRef.current.getBoundingClientRect().left + props.draggerWidth - 10
    ) {
      startXRef.current = event.clientX;
      setStartX(event.clientX);
      props.setIsResizing(true);
      props.setMousUpOnTimeline(false);
    }
  };

  const handleMouseUp = (event) => {
    // Document mouseup owns end-of-drag while resizing; this remains for local clicks.
    if (props.isResizing) return;
    props.setIsResizing(false);
    props.setMousUpOnTimeline(true);
    props.appData.updateGenericInfo({
      ...props.appData.genericInfo,
      draggerWidth: props.draggerWidth,
    });
  };

  const handleMouseMove = (event) => {
    // While resizing, document listeners handle width updates.
    if (props.isResizing) return;

    const resizableDiv = document.getElementById(resizableDivId);
    if (!resizableDiv) return;

    const cursorPosition =
      event.clientX - resizableDiv.getBoundingClientRect().left;
    if (props.movedOnMarker) {
      const newWidth = props.draggerWidth + (event.clientX - startX);
      let oneCM = props.totalDuration.divisions < 60 ? 45 : 44.97;
      if (newWidth < oneCM || newWidth > props.totalDuration.divisions * oneCM)
        return;
      props.setDraggerWidth(newWidth);
      setStartX(event.clientX);
    }
    if (cursorPosition >= props.draggerWidth - 8) {
      resizableDiv.style.cursor = "e-resize";
    } else {
      resizableDiv.style.cursor = "default";
    }
  };

  return (
    <div className="canvas-division">
      <canvas
        width={props.totalDuration.width + 10 + "px"}
        position="absolute"
        height="49px"
        margin-top="100px"
        ref={canvasRef}
        className="ruler"
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      ></canvas>
      <div
        id={resizableDivId}
        style={{
          width: `${props.draggerWidth}px`,
          height: "50px",
          backgroundColor: "rgba(9, 88, 181, 0.1)", // "#dae5f2",
          position: "absolute",
          top: isHitAreaModal ? 0 : 4,
          left: isHitAreaModal ? 5 : 6,
          cursor: props?.playPause?.animationCompleted ? "ew-resize" : "default",
          marginLeft: isHitAreaModal ? "0px" : "1px",
        }}
        onMouseDown={(event) =>
          props?.playPause?.animationCompleted &&
          handleMouseDown(event, "right")
        }
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      ></div>
    </div>
  );
}

export default Canvas;
